import MuiButtonBase from '@mui/material/ButtonBase';
import MuiDialog from '@mui/material/Dialog';
import MuiDialogActions from '@mui/material/DialogActions';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiIconButton from '@mui/material/IconButton';
import { alpha, styled } from '@mui/material/styles';
import { getPrismScrollbarStyles, getPrismScrollbarTargetStyles } from 'theme';

interface DialogSelectTriggerStyleProps {
  hasValue?: boolean;
}

export const DialogRoot = styled(MuiDialog)(({ theme }) => ({
  '--dialog-border-width': '1px',
  '--dialog-close-icon-size': '14px',
  '--dialog-close-icon-stroke-width': '2px',
  '--dialog-close-size': '20px',
  '--dialog-description-gap': '4px',
  '--dialog-description-font-size': '14px',
  '--dialog-description-line-height': '20px',
  '--dialog-footer-gap': '16px',
  '--dialog-padding': '24px',
  '--dialog-radius': '12px',
  '--dialog-select-trigger-gap': '8px',
  '--dialog-select-trigger-height': '40px',
  '--dialog-select-trigger-icon-size': '20px',
  '--dialog-select-trigger-padding-x': '8px',
  '--dialog-select-trigger-radius': '4px',
  '--dialog-title-font-size': '18px',
  '--dialog-title-line-height': '22px',
  '--dialog-viewport-margin': '32px',
  '--dialog-warning-gap': '6px',
  '--dialog-warning-icon-size': '16px',
  '--dialog-warning-margin-top': '6px',
  '& .MuiDialog-paper': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 'var(--dialog-radius)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.common.black, 0.1)}`,
    margin: 'var(--dialog-viewport-margin)',
    minWidth: 0,
    overflow: 'hidden',
  },
  ...getPrismScrollbarTargetStyles(theme, '& .MuiDialog-paper', {
    thumbBorderColor: theme.palette.background.paper,
  }),
}));

export const DialogHeader = styled('div')(({ theme }) => ({
  alignItems: 'center',
  borderBottom: `var(--dialog-border-width) solid ${theme.palette.neutral[200]}`,
  boxSizing: 'border-box',
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  padding: 'var(--dialog-padding)',
}));

export const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  '&&&': {
    color: theme.palette.neutral[600],
    flex: '1 1 auto',
    fontSize: 'var(--dialog-title-font-size) !important',
    fontWeight: '600 !important',
    lineHeight: 'var(--dialog-title-line-height) !important',
    minWidth: 0,
    overflow: 'hidden',
    padding: '0 !important',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export const DialogCloseButton = styled(MuiIconButton)(({ theme }) => ({
  color: theme.palette.neutral[500],
  flex: '0 0 auto',
  height: 'var(--dialog-close-size)',
  padding: 0,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 'var(--dialog-close-size)',
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.neutral[100],
    color: theme.palette.neutral[700],
  },
  '&.Mui-disabled': {
    color: theme.palette.action.disabled,
    cursor: 'not-allowed',
    pointerEvents: 'auto',
  },
}));

export const DialogCloseIcon = styled('span')({
  display: 'block',
  height: 'var(--dialog-close-icon-size)',
  position: 'relative',
  width: 'var(--dialog-close-icon-size)',
  '&::before, &::after': {
    backgroundColor: 'currentColor',
    borderRadius: 'var(--dialog-close-icon-stroke-width)',
    content: '""',
    height: 'var(--dialog-close-icon-stroke-width)',
    left: 0,
    position: 'absolute',
    top: '50%',
    width: '100%',
  },
  '&::before': {
    transform: 'translateY(-50%) rotate(45deg)',
  },
  '&::after': {
    transform: 'translateY(-50%) rotate(-45deg)',
  },
});

export const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  boxSizing: 'border-box',
  color: theme.palette.text.secondary,
  display: 'grid',
  gap: theme.spacing(2),
  fontSize: theme.typography.body2.fontSize,
  lineHeight: theme.typography.body2.lineHeight,
  overflowX: 'hidden',
  padding: 'var(--dialog-padding)',
  ...getPrismScrollbarStyles(theme, {
    thumbBorderColor: theme.palette.background.paper,
  }),
}));

export const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
  borderTop: `var(--dialog-border-width) solid ${theme.palette.neutral[200]}`,
  boxSizing: 'border-box',
  gap: 'var(--dialog-footer-gap)',
  justifyContent: 'flex-end',
  padding: 'var(--dialog-padding)',
  '& > :not(style) ~ :not(style)': {
    marginLeft: 0,
  },
}));

export const DialogDescription = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[500],
  display: 'grid',
  gap: 'var(--dialog-description-gap)',
  fontSize: 'var(--dialog-description-font-size)',
  lineHeight: 'var(--dialog-description-line-height)',
}));

export const DialogEmphasis = styled('strong')(({ theme }) => ({
  color: theme.palette.neutral[700],
  display: 'inline',
  fontWeight: 600,
  overflowWrap: 'anywhere',
}));

export const DialogWarning = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.red[600],
  display: 'inline-flex',
  gap: 'var(--dialog-warning-gap)',
  fontWeight: 500,
  lineHeight: 'var(--dialog-description-line-height)',
  marginTop: 'var(--dialog-warning-margin-top)',
}));

export const DialogWarningIcon = styled('span')({
  alignItems: 'center',
  display: 'inline-flex',
  flex: '0 0 auto',
  height: 'var(--dialog-warning-icon-size)',
  justifyContent: 'center',
  width: 'var(--dialog-warning-icon-size)',
  '& .MuiSvgIcon-root': {
    fontSize: 'var(--dialog-warning-icon-size)',
  },
});

export const DialogSelectTrigger = styled(MuiButtonBase, {
  shouldForwardProp: prop => prop !== 'hasValue',
})<DialogSelectTriggerStyleProps>(({ hasValue = false, theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  border: `var(--dialog-border-width) solid ${theme.palette.neutral[300]}`,
  borderRadius: 'var(--dialog-select-trigger-radius)',
  boxSizing: 'border-box',
  color: hasValue ? theme.palette.neutral[900] : theme.palette.neutral[500],
  display: 'flex',
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 400,
  gap: 'var(--dialog-select-trigger-gap)',
  height: 'var(--dialog-select-trigger-height)',
  justifyContent: 'space-between',
  lineHeight: theme.typography.body2.lineHeight,
  padding: '0 var(--dialog-select-trigger-padding-x)',
  textAlign: 'left',
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: '100%',
  '&:hover': {
    backgroundColor: theme.palette.neutral[50],
    borderColor: theme.palette.brand[400],
  },
  '&:focus-visible': {
    backgroundColor: theme.palette.brand[100],
    borderColor: theme.palette.brand[400],
    outline: 0,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    borderColor: theme.palette.neutral[200],
    color: theme.palette.action.disabled,
    opacity: 1,
  },
}));

export const DialogSelectTriggerValue = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const DialogSelectTriggerIcon = styled('span')({
  alignItems: 'center',
  display: 'inline-flex',
  flex: '0 0 auto',
  height: 'var(--dialog-select-trigger-icon-size)',
  justifyContent: 'center',
  width: 'var(--dialog-select-trigger-icon-size)',
  '& .MuiSvgIcon-root': {
    fontSize: 'var(--dialog-select-trigger-icon-size)',
  },
});
